import React, { useContext, useState, useRef, useEffect } from 'react';
import classNames from 'classnames';
import postMonetateImpression from '../../../utils/monetate/postMonetateImpression';
import NavigationPane from '../navigation-pane/NavigationPane';
import notifyAnalytics from '../notify-analytics';
import { isDesktop, isTouchDevice } from '../../../utils/device/identifyDevice';
import EnvVarsFeaturesContext from '../../../scaffold/EnvVarsFeaturesContext';

import styles from './flyout-item.scss';

const FlyoutItem = ({
  id,
  className,
  name,
  url,
  isDisplayedInGlobalNav,
  child = [],
  hierarchy = '',
  backLabel,
  toggleParentOpened,
  fetchAndStoreChildCategories,
  parentOpen,
  openClass = '',
  navigationV2Active,
  scrollToTop,
}) => {
  const depth = 1;
  const [opened, setOpened] = useState(false);
  const [removing, setRemoving] = useState(false);

  const {
    envVars: { monetate },
    features: { monetateExperiences },
  } = useContext(EnvVarsFeaturesContext);

  const hasNestedMenu = child && child.length > 0;
  const navigationPath = `${hierarchy ? `${hierarchy}|` : ''}${name}`;

  const toggleOpen = e => {
    e.preventDefault();
    if (fetchAndStoreChildCategories && !isDesktop()) {
      fetchAndStoreChildCategories(id);
    }
    toggleParentOpened(!opened);
    setOpened(!opened);
    scrollToTop();
  };

  const navigate = () => {
    notifyAnalytics(navigationPath);
  };

  const itemClassNames = classNames([
    className,
    styles.flyoutItem,
    {
      [openClass]: opened,
      [styles.mobile]: !isDisplayedInGlobalNav,
    },
  ]);

  const smoothRemove = () => {
    if (isDesktop()) {
      // iPad with desktop nav needs immediate removal
      setOpened(false);
      toggleParentOpened(false);
    } else {
      setRemoving(true);
      toggleParentOpened(false);
      window.setTimeout(() => {
        setOpened(false);
        setRemoving(false);
      }, 600);
    }
  };

  if (!hasNestedMenu && !url) {
    return null;
  }

  const handleMouseLeave = () => {
    if (isDesktop() && !isTouchDevice()) {
      setOpened(false);
    }
  };

  const innerRef = useRef(null);
  useEffect(() => {
    if (innerRef && innerRef.current) {
      innerRef.current.addEventListener('mouseleave', handleMouseLeave);
    }
  }, []);

  useEffect(() => {
    if (opened && name === 'Home & Garden') {
      postMonetateImpression(
        monetate,
        'navigation-experimental',
        monetateExperiences
      );
    } else if (opened) {
      postMonetateImpression(monetate, 'remove-dont-miss', monetateExperiences);
    }
  }, [opened]);

  return (
    <li
      ref={innerRef}
      className={itemClassNames}
      onMouseEnter={() => isDesktop() && !isTouchDevice() && setOpened(true)}
    >
      <a
        className={classNames([styles.link, styles.flyoutItem__heading])}
        href={url}
        onClick={hasNestedMenu && !opened ? toggleOpen : navigate}
      >
        {name}
      </a>
      {(opened || parentOpen) && hasNestedMenu && (
        <NavigationPane
          backLabel={backLabel}
          goBack={smoothRemove}
          id={id}
          categoryLink={url}
          name={name}
          depth={depth + 1}
          visible={opened && !removing}
          child={child}
          hierarchy={hierarchy}
          navigationV2Active={navigationV2Active}
          scrollToTop={scrollToTop}
        />
      )}
    </li>
  );
};

export default FlyoutItem;
